import { useEffect, useState } from 'react';
import { useEmployer } from '../context/EmployerContext';
import { useToast } from '../../toast/ToastContext';
import { parseCsv } from '../../icib-admin/utils/csv';
import Pagination, { usePagination } from '../../icib-admin/components/Pagination';
import ProductMultiSelect from '../../icib-admin/components/ProductMultiSelect';
import { withProductColumns, productDropdowns, prepareUploadRows, columnHeadings, withHeadings, fromHeadings } from '../../icib-admin/utils/productColumns';
import { phoneChars, resultRowEmail, todayISO } from '../../icib-admin/utils/format';
// utils/xlsx.js pulls in exceljs (a genuinely large library) purely to
// build/read the one .xlsx template with a product dropdown — dynamically
// imported below so it's its own chunk, only fetched when someone actually
// downloads or uploads an .xlsx. Same reasoning/module as ICIB-02's
// IntakeView, reused as-is rather than duplicated per dashboard.
const loadXlsxUtils = () => import('../../icib-admin/utils/xlsx');

// §2.6a — today's date as a YYYY-MM-DD default for effective_date: today or
// earlier applies the addition immediately (this form's normal path);
// anything later defers it to a PendingIntakeRow instead. Same helper/
// reasoning as ICIB-02's IntakeView.
function today() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Pacific/Auckland' }).format(new Date());
}

// Bulk-upload columns for an ADDITION row (BrandonUserStoryAPI.md EMP-03 —
// same bulk-upload endpoint/contract as ICIB-02a, just employer_admin-scoped
// to their own group). Only ADDITION — this view has no Update/Removal tab,
// unlike ICIB-02's IntakeView. `action` is pre-filled as a starter row so
// the only thing left to fill in per person is the actual data. `gender`
// (§2.1) is here too. The `subsidised_product_level` slot expands into one
// Yes/No column per product and `company_code` is dropped from the sheet
// (and not sent at upload) — see utils/productColumns.js. Column order
// matches ICIB-02's IntakeView (EXTR-13 — ICIB's Additions/Removals
// spreadsheet layout), with form_data fields slotted in after `gender`.
const BULK_COLUMNS = ['last_name', 'first_name', 'dob', 'gender', 'email', 'address', 'phone', 'effective_date', 'action', 'reason', 'subsidised_product_level'];
const BULK_DATE_COLUMNS = ['dob', 'effective_date'];

const EMPTY_FORM = { email: '', first_name: '', last_name: '', dob: '', phone: '', address: '', gender: '', subsidised_product_level: [], effective_date: '', reason: '' };
function emptyForm() {
  return { ...EMPTY_FORM, effective_date: today() };
}

// Same predicate as ICIB-02's IntakeView — a FormTemplate field this
// group-specific form still needs to render is anything that ISN'T one of
// the template's reserved marker fields (action/email/effective_date/reason/
// is_product_field, or one that maps straight onto a User profile field —
// now including gender — already collected above).
const NOT_A_FORM_DATA_FIELD = (f) => f.is_action_field || f.is_email_field || f.is_product_field || f.maps_to_user_field || f.is_effective_date_field || f.is_reason_field;

// ICIB-174 — the product picker is multi-select. The single-item
// POST /companies/{company_id}/employees only ever takes one product id, so one
// product still goes there; two or more go through the bulk-upload
// endpoint as a single ADDITION row carrying the id array (all-or-nothing
// across the row's products, one enrolment each), same one-row-bulk trick
// RemovalPanel/LeaversView use. Normalises both responses to
// `{ error, message }` or `{ enrolments: [...], scheduled: [...] }` —
// §2.6a/EMP-03: each product is only enrolled once effective_date + its
// eligibility_days has arrived, so one submit can enrol some products now
// and hold the rest (`partially_scheduled`), each held product with its
// own `apply_on`. The single-item 202 names no product, so its one held
// entry is the one product that was sent. Same helper as IntakeView's.
async function submitAddition(fields, selected, addOne, bulkUploadEmployees) {
  if (selected.length === 1) {
    const res = await addOne({ ...fields, subsidised_product_level: selected[0] });
    if (res?.error) return res;
    if (res?.status === 'scheduled') {
      return { enrolments: [], scheduled: [{ group_product_id: selected[0], effective_date: res.effective_date, apply_on: res.apply_on }] };
    }
    return { enrolments: [res], scheduled: [] };
  }
  const res = await bulkUploadEmployees('single-addition.csv', [{ ...fields, action: 'ADDITION', subsidised_product_level: selected }]);
  if (res?.error) return res;
  const row = res?.results?.[0];
  if (!res?.committed || !row || row.status === 'error') return { error: row?.status || 'rejected', message: row?.message };
  return { enrolments: row.enrolments || [], scheduled: row.scheduled || [] };
}

// Toast text for submitAddition's result — "Invited — 1 enrolment
// created. Scheduled — Life on 2026-10-30 …" for a partially_scheduled
// row; either half alone otherwise.
function additionMessage({ enrolments, scheduled }, productName) {
  const parts = [];
  if (enrolments.length) parts.push(`Invited — ${enrolments.length} enrolment${enrolments.length === 1 ? '' : 's'} created.`);
  if (scheduled.length) {
    const when = scheduled.map((s) => `${productName(s.group_product_id)} on ${s.apply_on || s.effective_date}`).join(', ');
    parts.push(`Scheduled — ${when}, once the eligibility period has passed.`);
  }
  return parts.join(' ');
}

// §2.6a — extra detail on a committed ADDITION row's result line: how
// many of a partially_scheduled row's products were enrolled now, and the
// earliest apply_on across its held products.
function rowDetail(r) {
  if (r.status !== 'scheduled' && r.status !== 'partially_scheduled') return '';
  const held = r.scheduled || [];
  const next = held.map((s) => s.apply_on || s.effective_date).filter(Boolean).sort()[0] || r.apply_on || r.effective_date;
  const parts = [];
  if (r.status === 'partially_scheduled') parts.push(`${(r.enrolments || []).length} enrolled now`);
  if (held.length) parts.push(`${held.length} scheduled, next applies ${next}`);
  else if (next) parts.push(`scheduled for ${next}`);
  return parts.length ? ` — ${parts.join(', ')}` : '';
}

/* EMP-03 — "The system shall allow employer administrators to submit new
   employee details through the portal," including as a bulk upload
   (BrandonUserStoryAPI.md EMP-03: "add options for adding employees as
   bulk"). Same underlying operations as ICIB-02a's Addition — single-item
   POST /companies/{company_id}/employees, plus the ADDITION-only slice of the
   shared POST /companies/{company_id}/employees/bulk-upload endpoint — just
   employer_admin-scoped to their own group, no new endpoints needed. No
   Update/Removal tabs here, unlike ICIB-02's IntakeView: those stay
   icib_admin/EMP-04 territory, not part of this story.

   ICIB-174 — the product picker is multi-select; see submitAddition above
   for how one vs several products are sent. */
function NewStarterView() {
  const {
    status, groupId, curGroup, groupProductsIn,
    activeTemplates, loadActiveTemplate, addOne, bulkUploadEmployees,
  } = useEmployer();
  const toast = useToast();
  const [form, setForm] = useState(emptyForm);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);

  // groupProducts now loads as part of EmployerContext's shared refresh()
  // batch for whichever group is selected — no per-view call needed here.
  useEffect(() => { if (groupId) loadActiveTemplate(groupId); }, [groupId, loadActiveTemplate]);

  const products = groupId ? groupProductsIn(groupId) : [];
  // undefined = not fetched yet for this group, null = fetched, group has no
  // active template — only the loaded-object case has fields to show.
  const tplEntry = groupId ? activeTemplates[groupId] : undefined;
  const tplFields = tplEntry ? tplEntry.fields : undefined;
  const customFields = tplFields ? tplFields.filter((f) => !NOT_A_FORM_DATA_FIELD(f)) : [];
  // README §2.6 Operation 2 ADDITION guard 3 — a group with no active
  // FormTemplate at all rejects every Addition (`no_form_template`) before
  // anything else is even checked, single-item or bulk. Disable both paths
  // rather than let the request round-trip to a guaranteed rejection.
  const noTemplate = tplEntry === null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCustomField = (k) => (e) => setCustom((c) => ({ ...c, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!groupId) return;
    const selected = form.subsidised_product_level;
    if (!selected.length) { toast('Select at least one product.', 'bad'); return; }
    setBusy(true);
    const res = await submitAddition({ ...form, ...custom }, selected, addOne, bulkUploadEmployees);
    setBusy(false);
    if (res?.error) {
      toast(res.message || 'Could not add this employee.', 'bad', res.error);
      return;
    }
    toast(additionMessage(res, (id) => products.find((p) => p.id === id)?.name || id), 'ok');
    setForm(emptyForm());
    setCustom({});
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Add Employee</div>
      <div className="page-h"><h2>Add an employee</h2></div>
      <p className="page-sub">Submit a new employee's details to start their enrolment in {curGroup()?.name || 'your employee benefits scheme'}.</p>

      {!groupId ? (
        <div className="card"><div className="empty"><b>No company assigned</b>Your account isn't currently assigned to a company.</div></div>
      ) : (
        <>
        <div className="card card-b">
          <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
            <h4>New addition — {curGroup()?.name}</h4>
            <span className="spacer" />
            <span className="ep">POST /companies/{'{company_id}'}/employees</span>
          </div>

          {noTemplate && (
            <div className="note bad" style={{ marginBottom: 14 }}>
              <b>No enrolment form yet</b>ICIB admin hasn't set up a form template for this company — the backend rejects every addition until one exists, so Add &amp; invite is disabled below.
            </div>
          )}

          <form onSubmit={submit}>
            <div className="grid g2">
              <div className="field">
                <label>Email <span className="req">*</span></label>
                <input type="email" required value={form.email} onChange={set('email')} placeholder="hemi.walker@example.co.nz" />
                <div className="hint">Looked up to resolve or create the person's account — not editable after intake.</div>
              </div>
              <div className="field">
                <label>First name <span className="req">*</span></label>
                <input required value={form.first_name} onChange={set('first_name')} />
              </div>
              <div className="field">
                <label>Last name <span className="req">*</span></label>
                <input required value={form.last_name} onChange={set('last_name')} />
              </div>
              <div className="field">
                <label>Date of birth <span className="req">*</span></label>
                <input type="date" required max={todayISO()} value={form.dob} onChange={set('dob')} />
                <div className="hint">Always required, even if this person already has an account.</div>
              </div>
              <div className="field">
                <label>Phone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: phoneChars(e.target.value) }))} />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Address</label>
                <input value={form.address} onChange={set('address')} />
              </div>
              <div className="field">
                <label>Gender <span className="req">*</span></label>
                <select required value={form.gender} onChange={set('gender')}>
                  <option value="" disabled>Select…</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="field">
                <label>Subsidised product level <span className="req">*</span></label>
                <ProductMultiSelect
                  products={products}
                  value={form.subsidised_product_level}
                  onChange={(ids) => setForm((f) => ({ ...f, subsidised_product_level: ids }))}
                  disabled={!products.length}
                />
                <div className="hint">Pick one or more — one enrolment is created per product.</div>
              </div>
              <div className="field">
                <label>Employment effective date <span className="req">*</span></label>
                <input type="date" required value={form.effective_date} onChange={set('effective_date')} />
                <div className="hint">Each product is enrolled (and its invitation sent) once this date plus that product's eligibility period has passed — until then it's scheduled.</div>
              </div>
              <div className="field">
                <label>Reason <span className="req">*</span></label>
                <input required value={form.reason} onChange={set('reason')} placeholder="e.g. New starter" />
              </div>
              {customFields.map((f) => (
                <div className="field" key={f.id}>
                  <label>{f.label}{f.required && <span className="req">*</span>}</label>
                  {f.field_type === 'dropdown' ? (
                    <select required={!!f.required} value={custom[f.field_key] || ''} onChange={setCustomField(f.field_key)}>
                      <option value="" disabled>Select…</option>
                      {(f.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      type={f.field_type === 'date' ? 'date' : f.field_type === 'number' ? 'number' : f.field_type === 'email' ? 'email' : 'text'}
                      required={!!f.required}
                      value={custom[f.field_key] || ''}
                      onChange={setCustomField(f.field_key)}
                    />
                  )}
                </div>
              ))}
            </div>

            {groupId && tplEntry === undefined && <div className="hint" style={{ marginBottom: 8 }}>Loading this company's custom fields…</div>}
            <div className="note" style={{ marginBottom: 14 }}>
              If this email already has an account, their existing name/DOB/phone/address/gender are kept
              as-is — an addition never overwrites a person's profile. Date of birth must still be entered
              as a valid date — it is always checked, even for an existing person. Creates one enrolment per product
              selected above — all or nothing: if they already hold an active enrolment for any
              one of them, none are created.
            </div>
            <div className="btn-row">
              <button type="submit" className="btn pri" disabled={busy || !products.length || noTemplate}>{busy ? 'Adding…' : 'Add'}</button>
            </div>
            {!products.length && <div className="hint" style={{ marginTop: 8 }}>This company has no active products attached yet — ask ICIB admin to add one before inviting anyone.</div>}
          </form>
        </div>

        <div className="sec-h"><h3>Or upload many at once</h3></div>
        <BulkAddPanel curGroup={curGroup} products={products} customFields={customFields} bulkUploadEmployees={bulkUploadEmployees} noTemplate={noTemplate} />
        </>
      )}
    </>
  );
}

// EMP-03 bulk — same underlying endpoint/contract as ICIB-02's IntakeView
// BulkUploadPanel (ADDITION tab), just employer_admin-scoped to their own
// group and with no Update/Removal tabs (out of EMP-03's scope — those stay
// icib_admin/EMP-04 territory). A file can still carry non-ADDITION rows
// (README §2.6 lets a file mix actions) — flagged as a soft warning below,
// same as ICIB's version, never a block.
function BulkAddPanel({ curGroup, products, customFields, bulkUploadEmployees, noTemplate }) {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  // A filled-in template can run to hundreds of rows — page the preview
  // rather than rendering the whole file at once. Upload still sends
  // every row, not just the visible page.
  const rowsPage = usePagination(rows);
  const [busy, setBusy] = useState(false);
  const [buildingXlsx, setBuildingXlsx] = useState(false);
  const [result, setResult] = useState(null);
  // The per-row log is as long as the file — paged like the preview.
  const resultPage = usePagination(result?.results || []);

  const headers = rows.length ? Object.keys(rows[0]) : [];
  const mismatched = rows.filter((r) => r.action && r.action.toUpperCase() !== 'ADDITION').length;
  const customFieldKeys = customFields.map((f) => f.field_key);
  // EXTR-13 — column headings follow ICIB's own sheet, same as IntakeView.
  const headings = columnHeadings(customFields, products);

  // Same in-cell-dropdown/date-formatting reasoning as ICIB-02's
  // IntakeView — a real dropdown for gender, any custom field_type
  // 'dropdown' field (e.g. nz_citizen_or_visa), and a Yes/No dropdown per
  // product column (ICIB-174 — folded back into subsidised_product_level
  // ids at upload time by toUploadRow) instead of a free-text box the
  // upload could reject; every date column
  // formatted YYYY-MM-DD with a header note, since Excel otherwise
  // free-formats a date column.
  async function onDownloadTemplate() {
    const cols = withProductColumns(BULK_COLUMNS.flatMap((c) => (c === 'gender' ? [c, ...customFieldKeys] : [c])), products);
    const starterRow = cols.map((c) => (c === 'action' ? 'ADDITION' : c === 'effective_date' ? today() : ''));
    const base = `${curGroup()?.company_code || 'company'}_addition_template`;
    const listDropdowns = [
      { header: 'gender', values: ['male', 'female'] },
      ...productDropdowns(products),
      ...customFields.filter((f) => f.field_type === 'dropdown' && f.options?.length).map((f) => ({ header: f.field_key, values: f.options })),
    ];
    const dateColumns = [...BULK_DATE_COLUMNS, ...customFields.filter((f) => f.field_type === 'date').map((f) => f.field_key)];
    setBuildingXlsx(true);
    try {
      const { downloadXlsxTemplate } = await loadXlsxUtils();
      await downloadXlsxTemplate({
        filename: `${base}.xlsx`,
        starterRow,
        icibStyle: true,
        ...withHeadings({ headers: cols, listDropdowns, dateColumns }, headings),
      });
    } finally {
      setBuildingXlsx(false);
    }
  }

  function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    rowsPage.setPage(1);
    if (/\.xlsx$/i.test(file.name)) {
      loadXlsxUtils().then(({ parseXlsx }) => file.arrayBuffer().then(parseXlsx)).then((parsed) => setRows(fromHeadings(parsed, headings)));
    } else {
      file.text().then((text) => setRows(fromHeadings(parseCsv(text), headings)));
    }
  }


  async function onUpload() {
    if (!rows.length) return;
    // ICIB-174 — fold each row's Yes/No product columns into the id array
    // the bulk endpoint expects; company_code isn't sent (utils/productColumns.js).
    const { rows: uploadRows, problems } = prepareUploadRows(rows, products);
    if (problems.length) { toast(problems.join(' '), 'bad'); return; }
    if (!uploadRows.length) { toast('No filled-in rows to upload.', 'bad'); return; }
    setBusy(true);
    const res = await bulkUploadEmployees(fileName, uploadRows);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not process this file.', 'bad', res.error); return; }
    setResult({ ...res, sentRows: uploadRows });
    resultPage.setPage(1);
    if (res.committed) {
      toast(`${res.rows_processed} row(s) committed.`, 'ok');
      setFileName(''); setRows([]);
    } else {
      toast('Some rows failed — nothing was written. Fix and re-upload.', 'bad');
    }
  }

  return (
    <div className="card card-b">
      <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 10 }}>
        <h4>Bulk addition</h4>
        <span className="spacer" />
        <span className="ep">POST /companies/{'{company_id}'}/employees/bulk-upload</span>
      </div>

      {noTemplate && (
        <div className="note bad" style={{ marginBottom: 12 }}>
          <b>No enrolment form yet</b>ICIB admin hasn't set up a form template for this company — the backend rejects every addition row (single or bulk) until one exists, so the template download and uploading are both disabled below.
        </div>
      )}

      <div className="btn-row" style={{ marginBottom: 6 }}>
        <button type="button" className="btn sm" onClick={onDownloadTemplate} disabled={buildingXlsx || noTemplate}>
          {buildingXlsx ? 'Building…' : 'Download addition template (.xlsx)'}
        </button>
      </div>
      <div className="hint" style={{ marginBottom: 12 }}>
        The template has just the columns an addition row needs — action pre-filled. Each
        product has its own column with a Yes/No dropdown — mark Yes for every product this
        person should be enrolled in. Date columns ({BULK_DATE_COLUMNS.map((c) => headings[c]).join(', ')}) are formatted YYYY-MM-DD with a note
        on the header cell, Biological Sex is a real in-cell dropdown limited to male/female{customFields.some((f) => f.field_type === 'dropdown') && ', and any custom dropdown field is a real in-cell dropdown too'}.
      </div>

      <div className="field">
        <label>Completed file</label>
        <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} disabled={noTemplate} />
        <div className="hint">The .xlsx template above, or a plain CSV with the same headers — either uploads the same way.</div>
      </div>

      {rows.length > 0 && (
        <>
          {mismatched > 0 && (
            <div className="note warn" style={{ marginBottom: 10 }}>
              {mismatched} row(s) have an action other than ADDITION — that's fine, each row is
              processed by its own action column.
            </div>
          )}
          <div className="tbl-wrap" style={{ marginBottom: 10 }}>
            <table>
              <thead><tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {rowsPage.pageItems.map((r, i) => (
                  <tr key={i}>{headers.map((h) => <td key={h} className="mono">{r[h]}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginBottom: 10 }}><Pagination {...rowsPage} /></div>
          <div className="btn-row">
            <button type="button" className="btn pri sm" disabled={busy || noTemplate} onClick={onUpload}>{busy ? 'Uploading…' : `Upload ${rows.length} row(s) as one batch`}</button>
          </div>
          <div className="hint" style={{ marginTop: 6 }}>
            Atomic (README §2.6) — every row must pass or nothing is written.
          </div>
        </>
      )}

      {result && (
        <div className={'note' + (result.committed ? ' ok' : ' bad')} style={{ marginTop: 12 }}>
          <b>{result.committed ? 'Committed' : 'Rejected — nothing written'}</b>
          {resultPage.pageItems.map((r) => (
            <div key={r.row} className="mono" style={{ fontSize: 11.5 }}>
              row {r.row} ({r.action}){resultRowEmail(result, r) && ` ${resultRowEmail(result, r)}`}: {r.status}{r.message ? ` — ${r.message}` : ''}
              {rowDetail(r)}
            </div>
          ))}
          <Pagination {...resultPage} />
        </div>
      )}
    </div>
  );
}

export default NewStarterView;
